import { useState } from "react"
import { Box, CircularProgress, Drawer, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation, faEllipsisV, faPen, faPrint } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { useQueryClient } from "@tanstack/react-query"

import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useApiQuery } from "../../../core/api/useApiQuery"
import {
    fetchOrderDetails,
    MeetingDetailsOrder,
    MeetingServiceQuery,
} from "../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import {
    MeetingServiceOrder,
    MeetingServiceOrdersView,
} from "../../../modules/merchant/merchant/production/meetingService/meetingServiceTypes"
import { MeetingServiceOrdersDependencies } from "../../../modules/merchant/merchant/production/meetingService/components/OrderDeliveryList"
import { SavedOrderInfo } from "../../../modules/merchant/merchant/production/meetingService/productionView/useProductionOrderEdit"
import { ChatMessageAuthorRole, ChatMessageSender } from "../orderChat/orderChatTypes"
import { StandardButton } from "../buttons/StandardButton"
import { OrderDrawerHeader, OrderDrawerHeaderAction } from "./OrderDrawerHeader"
import { OrderDetailsCard } from "./OrderDetailsCard"
import { OrderLinesReadSection } from "./OrderLinesReadSection"
import { ResourceBookingsSection } from "./ResourceBookingsSection"
import { OrderMessageSection } from "./OrderMessageSection"
import { OrderDocumentsMenu } from "./OrderDocumentsMenu"
import { OrderKebabMenu } from "./OrderKebabMenu"
import { MerchantOrderFooter } from "./MerchantOrderFooter"
import { MeetingServiceQuickEditDrawer } from "./MeetingServiceQuickEditDrawer"

function resolveOrderNote(orderDetails: MeetingDetailsOrder): string | undefined {
    const deliveryNotes = orderDetails.deliveries?.map((delivery) => delivery.orderNote) ?? []

    return [orderDetails.note, orderDetails.orderNote, ...deliveryNotes].find((candidate) => candidate?.trim())
}

const useStyles = makeStyles()((theme) => ({
    paper: {
        width: "34rem",
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: "1rem",
        background: theme.palette.tailwind.slate[100],
    },
    stateBox: {
        height: "100%",
    },
    errorIcon: {
        fontSize: "1.75rem",
        color: theme.palette.tailwind.slate[400],
    },
    errorText: {
        color: theme.palette.tailwind.slate[500],
    },
}))

export type MeetingServiceOrderDrawerOrder = Readonly<{
    id: number
    meetingDetails?: { meetingName?: string | null }
}>

export type MeetingServiceOrderDrawerMerchant = Readonly<{
    order: MeetingServiceOrder
    view: MeetingServiceOrdersView
    meetingServiceDependencies: MeetingServiceOrdersDependencies
    onSaved?: (info: SavedOrderInfo) => void
    onPackingSlip: () => void
    onReceipt: () => void
    onDuplicate: () => void
    onDelete: () => void
}>

type Props = Readonly<{
    open: boolean
    order: MeetingServiceOrderDrawerOrder
    viewerRole: ChatMessageAuthorRole
    sender?: ChatMessageSender
    onClose: () => void
    merchant?: MeetingServiceOrderDrawerMerchant
}>

export function MeetingServiceOrderDrawer({ open, order, viewerRole, sender, onClose, merchant }: Props) {
    const { classes } = useStyles()
    const queryClient = useQueryClient()

    const detailsQuery = useApiQuery(fetchOrderDetails, {
        queryName: MeetingServiceQuery.ORDER_DETAILS,
        dependencies: { orderId: order.id },
        enabled: open,
        select: (data): MeetingDetailsOrder | undefined => {
            const orderDetails = data.orders?.[0]
            if (!orderDetails) return undefined

            return { ...orderDetails, note: resolveOrderNote(orderDetails) }
        },
    })
    const details = detailsQuery.data

    const hasNoOrder = detailsQuery.isError || (!detailsQuery.isInitialLoading && !details)
    const hasMeetingServiceOrder = details?.orderSummary.hasCatering ?? false

    const [openSections, setOpenSections] = useState<Set<string>>(new Set(["lines"]))
    const toggleSection = (key: string) =>
        setOpenSections((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

    const [kebabAnchor, setKebabAnchor] = useState<HTMLElement | null>(null)
    const [documentsAnchor, setDocumentsAnchor] = useState<HTMLElement | null>(null)
    const [isQuickEditOpen, setIsQuickEditOpen] = useState(false)
    const [quickEditDeliveryTime, setQuickEditDeliveryTime] = useState<string>()

    const openQuickEdit = (deliveryTime?: string) => {
        setQuickEditDeliveryTime(deliveryTime)
        setIsQuickEditOpen(true)
    }

    const headerActions: OrderDrawerHeaderAction[] = merchant
        ? [
              {
                  icon: faPrint,
                  ariaLabel: "print",
                  onClick: (event) => setDocumentsAnchor(event.currentTarget),
              },
              { icon: faPen, ariaLabel: "edit", onClick: () => openQuickEdit() },
              { icon: faEllipsisV, ariaLabel: "more", onClick: (event) => setKebabAnchor(event.currentTarget) },
          ]
        : []

    return (
        <>
            <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
                <OrderDrawerHeader
                    title={order.meetingDetails?.meetingName ?? undefined}
                    orderId={order.id}
                    actions={headerActions}
                    onClose={onClose}
                />

                <Box className={classes.body}>
                    {hasNoOrder ? (
                        <Stack alignItems="center" justifyContent="center" spacing="1rem" className={classes.stateBox}>
                            <FontAwesomeIcon icon={faCircleExclamation} className={classes.errorIcon} />
                            <LocalizedStrict id="meeting-service-order-drawer-load-error">
                                <Typography variant="body2" className={classes.errorText}>
                                    Couldn`t load the order
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict id="meeting-service-order-drawer-retry">
                                <StandardButton
                                    fullWidth={false}
                                    variant="outlined"
                                    color="grey"
                                    onClick={() => detailsQuery.refetch()}
                                >
                                    Try again
                                </StandardButton>
                            </LocalizedStrict>
                        </Stack>
                    ) : !details ? (
                        <Stack alignItems="center" justifyContent="center" className={classes.stateBox}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <>
                            {hasMeetingServiceOrder && (
                                <>
                                    <OrderDetailsCard details={details} />

                                    <OrderLinesReadSection
                                        details={details}
                                        open={openSections.has("lines")}
                                        onToggle={() => toggleSection("lines")}
                                        onEdit={merchant ? openQuickEdit : undefined}
                                    />
                                </>
                            )}

                            <ResourceBookingsSection
                                details={details}
                                open={openSections.has("bookings")}
                                onToggle={() => toggleSection("bookings")}
                            />

                            {hasMeetingServiceOrder && (
                                <OrderMessageSection
                                    orderId={order.id}
                                    sender={sender}
                                    viewerRole={viewerRole}
                                    open={openSections.has("message")}
                                    onToggle={() => toggleSection("message")}
                                />
                            )}
                        </>
                    )}
                </Box>

                {merchant && (
                    <MerchantOrderFooter
                        order={merchant.order}
                        view={merchant.view}
                        meetingServiceDependencies={merchant.meetingServiceDependencies}
                    />
                )}
            </Drawer>

            {merchant && (
                <>
                    <OrderDocumentsMenu
                        anchorEl={documentsAnchor}
                        onClose={() => setDocumentsAnchor(null)}
                        onPackingSlip={merchant.onPackingSlip}
                        onReceipt={merchant.onReceipt}
                    />
                    <OrderKebabMenu
                        anchorEl={kebabAnchor}
                        onClose={() => setKebabAnchor(null)}
                        onDuplicate={merchant.onDuplicate}
                        onDelete={merchant.onDelete}
                    />
                </>
            )}

            {isQuickEditOpen && merchant && (
                <MeetingServiceQuickEditDrawer
                    open
                    order={merchant.order}
                    initialDeliveryTime={quickEditDeliveryTime}
                    onClose={() => setIsQuickEditOpen(false)}
                    onSaved={(info) => {
                        queryClient.invalidateQueries({ queryKey: [MeetingServiceQuery.ORDER_DETAILS] })
                        merchant.onSaved?.(info)
                    }}
                />
            )}
        </>
    )
}
